import {
  LANGUAGES_NAME,
  MIN_GRADE_TO_PASS,
} from "pages/student/activity/common/constants"
import { PLACEMENT_STATUS_LOG_TYPES } from "pages/student/placements/common/constants"
import React, { useContext, useEffect, useMemo, useState } from "react"
import usePlacementApi from "modules/placements"
import { useAuthContext } from "./AuthContext"
import { isRunningFromIframe } from "common/functions"

export const PlacementContext = React.createContext()
export const usePlacementContext = () => {
  // get the context
  const context = useContext(PlacementContext)
  // if the context is undefined, throw an error
  if (context === undefined) {
    throw new Error(
      "usePlacementContext must be used within a PlacementProvider"
    )
  }
  // return the context
  return context
}

const PlacementProvider = ({ children }) => {
  const [placementModules, setPlacementModules] = useState([]) // [{id: 1, name: "A1"}, {id: 2, name: "A2"}
  const [placementLanguage, setPlacementLanguage] = useState(
    LANGUAGES_NAME.ENGLISH
  ) // by default use english
  const [placementId, setPlacementId] = useState(-1)
  const [placementTestId, setPlacementTestId] = useState(-1)
  const [placementIsFinisihed, setPlacementIsFinished] = useState(false)
  const [placementFirstLoadFinished, setPlacementFirstLoadFinished] =
    useState(false)
  const [moduleIdSetted, setModuleIdSetted] = useState(false)
  const [resumeData, setResumeData] = useState(null)
  const { userData, selectedGroupData } = useAuthContext()
  const { addTest, editTest, addStatusLog } = usePlacementApi()
  const [prevRoute, setPrevRoute] = useState()
  const [allowLevelSkip, setAllowLevelSkip] = useState(true)

  useEffect(() => {
    if (placementTestId === null || placementTestId === undefined) {
      setPlacementTestId(-1)
    }
  }, [placementTestId])

  /**
   *
   * @param {*} levelObtained is the moduleId of the last passed test
   * @returns
   */
  const createPlacementTest = async ({
    levelObtained,
    grade,
    withResults = true,
  }) => {
    const response = await addTest({
      groupId: selectedGroupData?.groupId,
      placementId,
      userId: userData.userId,
      grade: 0,
      progress: 0,
      levelObtained,
      isSelected: false,
      results: withResults
        ? [
            {
              moduleId: levelObtained,
              grade,
            },
          ]
        : [],
      autoAssignToGroup: false,
      isFinished: false,
      version: 2,
      ...(placementTestId > 0 && { placementTestId }),
    })
    if (response?.status) {
      setPlacementTestId(response.placementTestId)
    }
    return response
  }

  const updatePlacementTest = async ({
    module,
    modules,
    logs = [],
    addLogs = true,
  }) => {
    const allModules = modules || placementModules
    const modulesDone = [...allModules].filter(
      (m) => m.isDone || m.progress === 100
    )
    if (modules) {
      setPlacementModules(modules)
    }
    if (placementTestId < 0) {
      return false
    }

    let testsValue = 0
    let allTestsValue = 0
    for (const test of allModules) {
      allTestsValue += test.value
      testsValue += (test.value * test?.grade) / 100
    }

    const generalGrade = Math.round((testsValue / allTestsValue) * 100)
    // const generalProgress = Math.round((modulesDone.length / allModules.length) * 100);

    const response = await editTest({
      groupId: selectedGroupData?.groupId,
      placementTestId,
      placementId,
      userId: userData.userId,
      grade: isNaN(generalGrade) ? 0 : generalGrade,
      progress: 100,
      levelObtained: module.moduleId,
      isSelected: false,
      isFinished: false,
      autoAssignToGroup: false,
      results: modulesDone.map((module) => ({
        moduleId: module.moduleId,
        grade: module.grade,
      })),
      version: 2,
      logs: addLogs ? logs : [],
    })
    return response
  }

  const finishPlacementTest = async ({
    module,
    modules,
    cache = {},
    version = 2,
  }) => {
    const runningFromIframe = isRunningFromIframe()
    if (
      runningFromIframe &&
      typeof window?.parent?.onPlacementEnd === "function"
    ) {
      window.parent?.onPlacementEnd()
    }
    const allModules = modules || placementModules
    console.log("🚀 ~ finishPlacementTest ~ allModules:", allModules)
    const modulesDone = [...allModules].filter(
      (m) => m.isDone || m.progress === 100
    )
    const lastModuleDone = modulesDone[modulesDone.length - 1]
    const lastModuleDoneIndex = allModules.findIndex(
      (m) => m.moduleId === lastModuleDone.moduleId
    )
    console.log(
      "🚀 ~ file: PlacementContext.js:116 ~ finishPlacementTest ~ modulesDone:",
      modulesDone
    )
    const failedTests = allModules
      .slice(lastModuleDoneIndex)
      .filter((m) => m.grade < MIN_GRADE_TO_PASS)
    // console.log("🚀 ~ file: PlacementContext.js:122 ~ finishPlacementTest ~ failedTests:", failedTests)
    const nextTest =
      failedTests.length > 0
        ? failedTests[0]
        : allModules[allModules.length - 1]

    if (modules) {
      setPlacementModules(modules)
    }
    let testId = cache?.placementTestId || placementTestId
    if (testId < 0) {
      const res = await createPlacementTest({
        levelObtained: module.moduleId,
        grade: module?.grade,
      })
      testId = res?.placementTestId
    }

    let testsValue = 0
    let allTestsValue = 0
    for (const test of modulesDone) {
      allTestsValue += test.value
      testsValue += (test.value * test?.grade) / 100
    }

    const generalGrade = Math.round((testsValue / allTestsValue) * 100)
    // const generalProgress = Math.round((modulesDone.length / allModules.length) * 100);

    const response = await editTest({
      groupId: selectedGroupData?.groupId,
      placementTestId: testId,
      placementId: cache?.placementId || placementId,
      userId: cache?.userId || userData.userId,
      grade: isNaN(generalGrade) ? 0 : generalGrade,
      progress: 100,
      levelObtained: version === 2 ? module?.moduleId : nextTest?.moduleId,
      isSelected: false,
      isFinished: true,
      autoAssignToGroup: true,
      results: modulesDone.map((module) => ({
        moduleId: module.moduleId,
        grade: module.grade,
      })),
      replaceCurrentGroup: true,
      version: 2,
    })
    console.log("~ finishPlacementTest ~ response:", response)
    setPlacementIsFinished(true)
  }

  const isModuleDone = (module) => {
    return (
      module.placementTestStatusId === PLACEMENT_STATUS_LOG_TYPES.SUCCESS ||
      module.placementTestStatusId === PLACEMENT_STATUS_LOG_TYPES.FAILED ||
      module.placementTestStatusId === PLACEMENT_STATUS_LOG_TYPES.REASSIGNED
      // module.placementTestStatusId === PLACEMENT_STATUS_LOG_TYPES.SKIPPED_BY_PERFORMANCE
    )
  }

  const updateModulesStatus = ({
    allModules = [],
    currentModuleIndex,
    addLogs = true,
  }) => {
    return new Promise((resolve, reject) => {
      ;(async () => {
        try {
          console.log("🚀 ~ updateModulesStatus ~ allModules:", allModules)
          const currentModule = allModules[currentModuleIndex]
          console.log("🚀 ~ updateModulesStatus ~ currentModule:", {
            currentModule,
          })
          if (currentModule.progress !== 100) {
            allModules[currentModuleIndex].placementTestStatusId =
              PLACEMENT_STATUS_LOG_TYPES.ONPROGRESS
            if (addLogs) {
              await addStatusLog({
                placementTestResultId: currentModule.placementTestResultId,
                statusId: PLACEMENT_STATUS_LOG_TYPES.ONPROGRESS,
              })
            }
            return resolve({
              nextModuleId: null,
              isExpectedToBeCompleted: false,
              expectedLevel: null,
              modules: allModules,
              logs: [
                {
                  placementTestResultId: currentModule.placementTestResultId,
                  placementTestStatusId: PLACEMENT_STATUS_LOG_TYPES.ONPROGRESS,
                },
              ],
            })
          }
          const isLastModule = currentModuleIndex === allModules.length - 1
          const prevModule = allModules[currentModuleIndex - 1]
          const prevModule2 = allModules[currentModuleIndex - 2]
          const nextModule = allModules[currentModuleIndex + 1]
          const nextModule2 = allModules[currentModuleIndex + 2]
          const canGoBackOneModule =
            currentModuleIndex > 0 &&
            !isModuleDone(prevModule) &&
            allowLevelSkip
          const canGoBackTwoModules =
            currentModuleIndex > 1 &&
            !isModuleDone(prevModule2) &&
            allowLevelSkip
          const canGoNextModule =
            currentModuleIndex + 1 < allModules.length &&
            !isModuleDone(nextModule)
          const canGoNextModule2 =
            currentModuleIndex + 2 < allModules.length &&
            !isModuleDone(nextModule2) &&
            allowLevelSkip
          console.log({
            canGoBackOneModule,
            canGoBackTwoModules,
            canGoNextModule,
            canGoNextModule2,
          })
          console.log({
            canGoBackOneModule: prevModule?.moduleName,
            canGoBackTwoModules: prevModule2?.moduleName,
            canGoNextModule: nextModule?.moduleName,
            canGoNextModule2: nextModule2?.moduleName,
          })
          if (currentModule.grade >= 90 && canGoNextModule2) {
            // check if i have any module with progress before nextModule2
            const nexts = allModules.slice(currentModuleIndex + 1)
            let anyWithProgress = nexts.find(
              (m) => m.progress === 100 && m.grade < MIN_GRADE_TO_PASS
            )
            if (!anyWithProgress) {
              anyWithProgress = nexts.find((m) => m.progress === 100)
            }
            if (nextModule?.progress === 100) {
              if (addLogs) {
                await addStatusLog({
                  placementTestResultId: currentModule.placementTestResultId,
                  statusId: PLACEMENT_STATUS_LOG_TYPES.SUCCESS,
                })
              }
              allModules[currentModuleIndex].placementTestStatusId =
                PLACEMENT_STATUS_LOG_TYPES.SUCCESS
              return resolve({
                nextModuleId: -1,
                isExpectedToBeCompleted: true,
                expectedLevel: anyWithProgress,
                modules: allModules,
                logs: [
                  {
                    placementTestResultId: currentModule.placementTestResultId,
                    placementTestStatusId: PLACEMENT_STATUS_LOG_TYPES.SUCCESS,
                  },
                ],
              })
            }

            if (addLogs) {
              await addStatusLog({
                placementTestResultId: currentModule.placementTestResultId,
                statusId: PLACEMENT_STATUS_LOG_TYPES.SUCCESS,
              })
              await addStatusLog({
                placementTestResultId: nextModule.placementTestResultId,
                statusId: PLACEMENT_STATUS_LOG_TYPES.SKIPPED_BY_PERFORMANCE,
                moveToModuleId: nextModule2.moduleId,
              })
              await addStatusLog({
                placementTestResultId: nextModule2.placementTestResultId,
                statusId: PLACEMENT_STATUS_LOG_TYPES.ONPROGRESS,
              })
            }
            allModules[currentModuleIndex].placementTestStatusId =
              PLACEMENT_STATUS_LOG_TYPES.SUCCESS
            allModules[currentModuleIndex + 1].placementTestStatusId =
              PLACEMENT_STATUS_LOG_TYPES.SKIPPED_BY_PERFORMANCE
            allModules[currentModuleIndex + 2].placementTestStatusId =
              PLACEMENT_STATUS_LOG_TYPES.ONPROGRESS
            return resolve({
              nextModuleId: nextModule2.moduleId,
              isExpectedToBeCompleted: false,
              expectedLevel: nextModule2,
              modules: allModules,
              logs: [
                {
                  placementTestResultId: currentModule.placementTestResultId,
                  placementTestStatusId: PLACEMENT_STATUS_LOG_TYPES.SUCCESS,
                },
                {
                  placementTestResultId: nextModule.placementTestResultId,
                  placementTestStatusId:
                    PLACEMENT_STATUS_LOG_TYPES.SKIPPED_BY_PERFORMANCE,
                  moduleId: nextModule2.moduleId,
                },
                {
                  placementTestResultId: nextModule2.placementTestResultId,
                  placementTestStatusId: PLACEMENT_STATUS_LOG_TYPES.ONPROGRESS,
                },
              ],
            })
          }
          if (
            currentModule.grade >= 50 &&
            currentModule.grade < MIN_GRADE_TO_PASS
          ) {
            if (addLogs) {
              await addStatusLog({
                placementTestResultId: currentModule.placementTestResultId,
                statusId: PLACEMENT_STATUS_LOG_TYPES.FAILED,
              })
            }
            allModules[currentModuleIndex].placementTestStatusId =
              PLACEMENT_STATUS_LOG_TYPES.FAILED
            const levelObtainedModule = currentModule
            return resolve({
              nextModuleId: -1,
              isExpectedToBeCompleted: true,
              expectedLevel: levelObtainedModule,
              modules: allModules,
              logs: [
                {
                  placementTestResultId: currentModule.placementTestResultId,
                  placementTestStatusId: PLACEMENT_STATUS_LOG_TYPES.FAILED,
                },
              ],
            })
          }
          if (
            currentModule.grade >= 30 &&
            currentModule.grade < 50 &&
            canGoBackOneModule
          ) {
            // const nextsOfPrev = allModules.slice(currentModuleIndex + 1)
            // const nextPassed = nextsOfPrev.find((m) => m.grade >= MIN_GRADE_TO_PASS)
            // if (nextPassed) {
            //   if (addLogs) {
            //     await addStatusLog({
            //       placementTestResultId: currentModule.placementTestResultId,
            //       statusId: PLACEMENT_STATUS_LOG_TYPES.FAILED,
            //     })
            //   }
            //   allModules[currentModuleIndex].placementTestStatusId =
            //     PLACEMENT_STATUS_LOG_TYPES.FAILED
            //   return resolve({
            //     nextModuleId: prevModule.moduleId,
            //     isExpectedToBeCompleted: true,
            //     modules: allModules,
            //     logs: [
            //       {
            //         placementTestResultId: currentModule.placementTestResultId,
            //         placementTestStatusId: PLACEMENT_STATUS_LOG_TYPES.FAILED,
            //       },
            //     ],
            //     expectedLevel: nextPassed,
            //   })
            // }
            if (addLogs) {
              await addStatusLog({
                placementTestResultId: currentModule.placementTestResultId,
                statusId: PLACEMENT_STATUS_LOG_TYPES.FAILED,
              })
              await addStatusLog({
                placementTestResultId: currentModule.placementTestResultId,
                statusId: PLACEMENT_STATUS_LOG_TYPES.REASSIGNED,
                moveToModuleId: prevModule.moduleId,
              })
              await addStatusLog({
                placementTestResultId: prevModule.placementTestResultId,
                statusId: PLACEMENT_STATUS_LOG_TYPES.ONPROGRESS,
              })
            }
            allModules[currentModuleIndex].placementTestStatusId =
              PLACEMENT_STATUS_LOG_TYPES.REASSIGNED
            allModules[currentModuleIndex - 1].placementTestStatusId =
              PLACEMENT_STATUS_LOG_TYPES.ONPROGRESS
            return resolve({
              nextModuleId: prevModule.moduleId,
              isExpectedToBeCompleted: false,
              modules: allModules,
              messageType: "dropped",
              logs: [
                {
                  placementTestResultId: currentModule.placementTestResultId,
                  placementTestStatusId: PLACEMENT_STATUS_LOG_TYPES.FAILED,
                },
                {
                  placementTestResultId: currentModule.placementTestResultId,
                  placementTestStatusId: PLACEMENT_STATUS_LOG_TYPES.REASSIGNED,
                  moduleId: prevModule.moduleId,
                },
                {
                  placementTestResultId: prevModule.placementTestResultId,
                  placementTestStatusId: PLACEMENT_STATUS_LOG_TYPES.ONPROGRESS,
                },
              ],
            })
          }
          if (
            currentModule.grade < 30 &&
            (canGoBackTwoModules || canGoBackOneModule)
          ) {
            // chek if any of the previous modules has progress
            const prevs = allModules.slice(0, currentModuleIndex)
            // find the last module with progress
            const lastWithProgress = [...prevs]
              .reverse()
              .find((m) => m.progress === 100)
            if (
              lastWithProgress &&
              !canGoBackOneModule &&
              canGoBackTwoModules
            ) {
              if (addLogs) {
                await addStatusLog({
                  placementTestResultId: currentModule.placementTestResultId,
                  statusId: PLACEMENT_STATUS_LOG_TYPES.FAILED,
                })
              }
              allModules[currentModuleIndex].placementTestStatusId =
                PLACEMENT_STATUS_LOG_TYPES.FAILED
              return resolve({
                nextModuleId: prevModule.moduleId,
                isExpectedToBeCompleted: true,
                modules: allModules,
                logs: [
                  {
                    placementTestResultId: currentModule.placementTestResultId,
                    placementTestStatusId: PLACEMENT_STATUS_LOG_TYPES.FAILED,
                  },
                ],
                expectedLevel: currentModule,
              })
            }
            // // check if any of the nexts module (next of prevModule) has grade > MIN_GRADE_TO_PASS
            // const nextsOfPrev = allModules.slice(currentModuleIndex + 1)
            // const nextPassed = nextsOfPrev.find(
            //   (m) => m.grade >= MIN_GRADE_TO_PASS
            // )
            // if (nextPassed) {
            //   if (addLogs) {
            //     await addStatusLog({
            //       placementTestResultId: currentModule.placementTestResultId,
            //       statusId: PLACEMENT_STATUS_LOG_TYPES.FAILED,
            //     })
            //   }
            //   allModules[currentModuleIndex].placementTestStatusId =
            //     PLACEMENT_STATUS_LOG_TYPES.FAILED
            //   return resolve({
            //     nextModuleId: prevModule.moduleId,
            //     isExpectedToBeCompleted: true,
            //     modules: allModules,
            //     logs: [
            //       {
            //         placementTestResultId: currentModule.placementTestResultId,
            //         placementTestStatusId: PLACEMENT_STATUS_LOG_TYPES.FAILED,
            //       },
            //     ],
            //     expectedLevel: nextPassed,
            //   })
            // }
            if (addLogs) {
              await addStatusLog({
                placementTestResultId: currentModule.placementTestResultId,
                statusId: PLACEMENT_STATUS_LOG_TYPES.FAILED,
              })
              await addStatusLog({
                placementTestResultId: currentModule.placementTestResultId,
                statusId: PLACEMENT_STATUS_LOG_TYPES.REASSIGNED,
                moveToModuleId: canGoBackTwoModules
                  ? prevModule2.moduleId
                  : prevModule.moduleId,
              })
              await addStatusLog({
                placementTestResultId: canGoBackTwoModules
                  ? prevModule2.placementTestResultId
                  : prevModule.placementTestResultId,
                statusId: PLACEMENT_STATUS_LOG_TYPES.ONPROGRESS,
              })
            }
            allModules[currentModuleIndex].placementTestStatusId =
              PLACEMENT_STATUS_LOG_TYPES.REASSIGNED
            if (canGoBackTwoModules) {
              allModules[currentModuleIndex - 2].placementTestStatusId =
                PLACEMENT_STATUS_LOG_TYPES.ONPROGRESS
            } else {
              allModules[currentModuleIndex - 1].placementTestStatusId =
                PLACEMENT_STATUS_LOG_TYPES.ONPROGRESS
            }
            return resolve({
              nextModuleId: canGoBackTwoModules
                ? prevModule2.moduleId
                : prevModule.moduleId,
              isExpectedToBeCompleted: false,
              modules: allModules,
              messageType: canGoBackTwoModules ? "low_performance" : "dropped",
              logs: [
                {
                  placementTestResultId: currentModule.placementTestResultId,
                  placementTestStatusId: PLACEMENT_STATUS_LOG_TYPES.FAILED,
                },
                {
                  placementTestResultId: currentModule.placementTestResultId,
                  placementTestStatusId: PLACEMENT_STATUS_LOG_TYPES.REASSIGNED,
                  moduleId: canGoBackTwoModules
                    ? prevModule2.moduleId
                    : prevModule.moduleId,
                },
                {
                  placementTestResultId: canGoBackTwoModules
                    ? prevModule2.placementTestResultId
                    : prevModule.placementTestResultId,
                  placementTestStatusId: PLACEMENT_STATUS_LOG_TYPES.ONPROGRESS,
                },
              ],
            })
          }
          if (
            (currentModule.grade >= MIN_GRADE_TO_PASS && !canGoNextModule) ||
            isLastModule
          ) {
            if (addLogs) {
              await addStatusLog({
                placementTestResultId: currentModule.placementTestResultId,
                statusId: PLACEMENT_STATUS_LOG_TYPES.SUCCESS,
              })
            }
            allModules[currentModuleIndex].placementTestStatusId =
              PLACEMENT_STATUS_LOG_TYPES.SUCCESS
            let levelObtainedModule = currentModule
            if (
              nextModule?.placementTestStatusId ===
                PLACEMENT_STATUS_LOG_TYPES.SUCCESS ||
              nextModule?.placementTestStatusId ===
                PLACEMENT_STATUS_LOG_TYPES.FAILED ||
              nextModule?.placementTestStatusId ===
                PLACEMENT_STATUS_LOG_TYPES.REASSIGNED
            ) {
              levelObtainedModule = nextModule
            }
            return resolve({
              nextModuleId: -1,
              isExpectedToBeCompleted: true,
              modules: allModules,
              expectedLevel: levelObtainedModule,
              logs: [
                {
                  placementTestResultId: currentModule.placementTestResultId,
                  placementTestStatusId: PLACEMENT_STATUS_LOG_TYPES.SUCCESS,
                },
              ],
            })
          }
          if (currentModule.grade >= MIN_GRADE_TO_PASS && canGoNextModule) {
            if (addLogs) {
              await addStatusLog({
                placementTestResultId: currentModule.placementTestResultId,
                statusId: PLACEMENT_STATUS_LOG_TYPES.SUCCESS,
              })
              await addStatusLog({
                placementTestResultId: nextModule.placementTestResultId,
                statusId: PLACEMENT_STATUS_LOG_TYPES.ONPROGRESS,
              })
            }
            allModules[currentModuleIndex].placementTestStatusId =
              PLACEMENT_STATUS_LOG_TYPES.SUCCESS
            allModules[currentModuleIndex + 1].placementTestStatusId =
              PLACEMENT_STATUS_LOG_TYPES.ONPROGRESS
            return resolve({
              nextModuleId: nextModule.moduleId,
              isExpectedToBeCompleted: false,
              expectedLevel: nextModule,
              modules: allModules,
              logs: [
                {
                  placementTestResultId: currentModule.placementTestResultId,
                  placementTestStatusId: PLACEMENT_STATUS_LOG_TYPES.SUCCESS,
                },
                {
                  placementTestResultId: nextModule.placementTestResultId,
                  placementTestStatusId: PLACEMENT_STATUS_LOG_TYPES.ONPROGRESS,
                },
              ],
            })
          }
          if (addLogs) {
            await addStatusLog({
              placementTestResultId: currentModule.placementTestResultId,
              statusId:
                currentModule.grade < MIN_GRADE_TO_PASS
                  ? PLACEMENT_STATUS_LOG_TYPES.FAILED
                  : PLACEMENT_STATUS_LOG_TYPES.SUCCESS,
            })
          }
          allModules[currentModuleIndex].placementTestStatusId =
            currentModule.grade < MIN_GRADE_TO_PASS
              ? PLACEMENT_STATUS_LOG_TYPES.FAILED
              : PLACEMENT_STATUS_LOG_TYPES.SUCCESS
          let levelObtainedModule = currentModule
          if (
            currentModule.grade >= MIN_GRADE_TO_PASS &&
            (nextModule?.placementTestStatusId ===
              PLACEMENT_STATUS_LOG_TYPES.SUCCESS ||
              nextModule?.placementTestStatusId ===
                PLACEMENT_STATUS_LOG_TYPES.FAILED ||
              nextModule?.placementTestStatusId ===
                PLACEMENT_STATUS_LOG_TYPES.REASSIGNED)
          ) {
            levelObtainedModule = nextModule
          }
          return resolve({
            nextModuleId: nextModule?.moduleId || currentModule?.moduleId,
            isExpectedToBeCompleted:
              currentModule.grade < MIN_GRADE_TO_PASS || isLastModule,
            expectedLevel: levelObtainedModule,
            modules: allModules,
            logs: [
              {
                placementTestResultId: currentModule.placementTestResultId,
                placementTestStatusId:
                  currentModule.grade < MIN_GRADE_TO_PASS
                    ? PLACEMENT_STATUS_LOG_TYPES.FAILED
                    : PLACEMENT_STATUS_LOG_TYPES.SUCCESS,
              },
            ],
          })
        } catch (error) {
          // Si ocurre un error, resolvemos la promesa con failed
          return reject(error)
        }
      })()
    })
  }

  const getPlacementExpectedLevel = ({ allModules = [] }) => {
    // return {
    //   module: allModules[allModules.length - 1],
    //   isExpectedToBeCompleted: true
    // }
    // get first module with status pending or onprogress
    const pendingModule = allModules.find(
      (module) =>
        module.placementTestStatusId === PLACEMENT_STATUS_LOG_TYPES.PENDING ||
        module.placementTestStatusId === PLACEMENT_STATUS_LOG_TYPES.ONPROGRESS
    )
    const modulesDone = allModules.filter(
      (module) =>
        module.progress === 100 &&
        (module.placementTestStatusId === PLACEMENT_STATUS_LOG_TYPES.SUCCESS ||
          module.placementTestStatusId === PLACEMENT_STATUS_LOG_TYPES.FAILED ||
          module.placementTestStatusId ===
            PLACEMENT_STATUS_LOG_TYPES.REASSIGNED)
    )
    let lastModuleDone = modulesDone[modulesDone.length - 1]
    if (pendingModule?.progress === 100) {
      lastModuleDone = pendingModule
      const grade = pendingModule.grade
      if (grade >= MIN_GRADE_TO_PASS) {
        lastModuleDone.placementTestStatusId =
          PLACEMENT_STATUS_LOG_TYPES.SUCCESS
      } else {
        lastModuleDone.placementTestStatusId = PLACEMENT_STATUS_LOG_TYPES.FAILED
      }
    }
    if (
      lastModuleDone.placementTestStatusId ===
        PLACEMENT_STATUS_LOG_TYPES.FAILED ||
      lastModuleDone.placementTestStatusId ===
        PLACEMENT_STATUS_LOG_TYPES.REASSIGNED
    ) {
      // check if i can go back one or two modules
      const lastModuleDoneIndex = allModules.findIndex(
        (m) => m.moduleId === lastModuleDone.moduleId
      )
      const prevModule = allModules[lastModuleDoneIndex - 1]
      const canGoBackOneModule =
        lastModuleDoneIndex > 0 && !isModuleDone(prevModule)
      const prevModule2 = allModules[lastModuleDoneIndex - 2]
      const canGoBackTwoModules =
        lastModuleDoneIndex > 1 && !isModuleDone(prevModule2)
      const grade = lastModuleDone.grade
      if (grade >= 50 && grade < MIN_GRADE_TO_PASS) {
        return {
          module: lastModuleDone,
          isExpectedToBeCompleted: true,
        }
      }
      if (grade >= 30 && grade < 50 && canGoBackOneModule) {
        return {
          module: prevModule,
          isExpectedToBeCompleted: false,
        }
      }
      if (grade < 30 && (canGoBackTwoModules || canGoBackOneModule)) {
        return {
          module: canGoBackTwoModules ? prevModule2 : prevModule,
          isExpectedToBeCompleted: false,
        }
      }
      return {
        module: lastModuleDone,
        isExpectedToBeCompleted: true,
      }
    }
    if (
      lastModuleDone.placementTestStatusId ===
      PLACEMENT_STATUS_LOG_TYPES.SUCCESS
    ) {
      // check if i can go next one or two modules
      const lastModuleDoneIndex = allModules.findIndex(
        (m) => m.moduleId === lastModuleDone.moduleId
      )
      const nextModule = allModules[lastModuleDoneIndex + 1]
      const canGoNextModule =
        lastModuleDoneIndex + 1 < allModules.length && !isModuleDone(nextModule)
      const nextModule2 = allModules[lastModuleDoneIndex + 2]
      const canGoNextModule2 =
        lastModuleDoneIndex + 2 < allModules.length &&
        !isModuleDone(nextModule2)
      const grade = lastModuleDone.grade
      if (grade >= 90 && (canGoNextModule || canGoNextModule2)) {
        return {
          module: canGoNextModule2 ? nextModule2 : nextModule,
          isExpectedToBeCompleted: false,
        }
      }
      if (grade >= MIN_GRADE_TO_PASS && grade < 90 && canGoNextModule) {
        return {
          module: nextModule,
          isExpectedToBeCompleted: false,
        }
      }
      return {
        module: lastModuleDone,
        isExpectedToBeCompleted: true,
      }
    }
    return {
      module: pendingModule || allModules[0],
      isExpectedToBeCompleted: false,
    }
  }

  // useEffect(() => {
  //   console.log(
  //     "🚀 ~ file: PlacementContext.js:98 ~ PlacementProvider ~ placementModules:",
  //     placementModules
  //   )
  // }, [placementModules])

  const value = useMemo(
    () => ({
      placementLanguage,
      placementId,
      placementTestId,
      placementModules,
      placementIsFinisihed,
      resumeData,
      placementFirstLoadFinished,
      moduleIdSetted,
      prevRoute,
      allowLevelSkip,
      setPlacementModules,
      setPlacementLanguage,
      setPlacementId,
      setPlacementTestId,
      createPlacementTest,
      finishPlacementTest,
      updatePlacementTest,
      setPlacementIsFinished,
      setResumeData,
      setPlacementFirstLoadFinished,
      setModuleIdSetted,
      updateModulesStatus,
      setPrevRoute,
      getPlacementExpectedLevel,
      setAllowLevelSkip,
    }),
    [
      placementLanguage,
      placementId,
      placementTestId,
      placementModules,
      placementIsFinisihed,
      resumeData,
      placementFirstLoadFinished,
      moduleIdSetted,
      prevRoute,
      allowLevelSkip,
    ]
  )

  return (
    <PlacementContext.Provider value={value}>
      {children}
    </PlacementContext.Provider>
  )
}

export default PlacementProvider
